<template>
  <div class="pa-2">
    <v-card outlined flat>
      <v-card-text class="pt-0 mt-0">
        <v-subheader>{{ $t("message.filters") }}</v-subheader>
        <v-row v-if="availableLabels">
          <v-col>
            <tags-combo v-model="labelFilter" :label="$t('message.label')" :items="availableLabels"></tags-combo>
          </v-col>
          <v-col>
            <BeltsSelect v-model="beltFilter" :hide-details="true"></BeltsSelect>
          </v-col>
          <v-col>
            <v-select clearable @change="searchUsers()" hide-details v-model="genderFilter" :items="genderOptions"
              :label="$t('message.gender')" dense outlined>
            </v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-autocomplete clearable @change="searchUsers();" hide-details small-chips :loading="loadingLists"
              :items="availableSports" chips v-model="sportFilter" :label="$t('message.sport')" dense outlined>
            </v-autocomplete>
          </v-col>
          <v-col>
            <v-autocomplete clearable @change="searchUsers();" hide-details small-chips :loading="loadingLists"
              :items="availableLevels" chips v-model="levelFilter" :label="$t('message.level')" dense outlined>
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-autocomplete @change="searchUsers(); getAvailableValues()" clearable hide-details small-chips
              :loading="loadingLists" :items="availableKeys" chips v-model="keyFilter"
              :label="$t('message.keys_filter')" dense outlined>
            </v-autocomplete>
          </v-col>
          <v-col>
            <v-autocomplete clearable @change="searchUsers();" hide-details small-chips :loading="loadingLists"
              :items="availableValues" chips v-model="valueFilter" :label="$t('message.values_filter')" dense outlined>
            </v-autocomplete>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-card v-if="loading" class="mt-2" flat outlined>
      <v-skeleton-loader type="list-item-avatar, list-item-avatar, list-item-avatar"></v-skeleton-loader>
    </v-card>
    <v-card v-else class="mt-2" flat outlined>
      <v-list>
        <v-subheader>
          {{ $t("message.total") }}: {{ users.length }}<span v-if="hasMore || users.length < total"> / {{ total }}</span>
        </v-subheader>
        <v-list-item @click="goToUserPage(u)" v-for="(u, index) in users" :key="'user_' + index + '_' + u._id">
          <v-list-item-action>
            <v-icon v-if="u.type == 'visitor'">mdi-target-account</v-icon>
            <v-icon v-else-if="u.type == 'prospect'">mdi-file-document-edit-outline</v-icon>
            <v-icon color="primary" v-else>mdi-account-outline</v-icon>
          </v-list-item-action>
          <v-list-item-content class="text-left">
            <v-list-item-title>{{ u.name }}</v-list-item-title>
            <v-list-item-subtitle v-if="u.joinedDate">{{ $t("message.joined") }}: {{ $d(new Date(u.joinedDate),
              "month_day_year", $i18n.locale) }}</v-list-item-subtitle>
            <v-list-item-subtitle v-else-if="u.firstVisit">{{ $t("message.first_visit") }}: {{ $d(new
              Date(u.firstVisit), "month_day_year", $i18n.locale) }}</v-list-item-subtitle>
            <v-list-item-subtitle v-else-if="u.created">{{ $t("message.added") }}: {{ $d(new Date(u.created),
              "month_day_year", $i18n.locale) }}</v-list-item-subtitle>
            <v-list-item-subtitle v-if="u.key && u.keyValue">
              <v-chip small><span class="font-weight-black mr-1">{{ u.key }}:</span> <span>{{ u.keyValue
              }}</span></v-chip>
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="u.labels">
              <v-chip small v-for="(label, labelIndex) in u.labels" :key="'user_' + index + '_label_' + labelIndex">{{
                label }}</v-chip>
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-avatar v-if="u.image">
            <img :src="userImg(u.image)" />
          </v-list-item-avatar>
        </v-list-item>
      </v-list>
      <v-card-actions v-if="hasMore" class="justify-center pb-3">
        <v-btn outlined small elevation="0" color="primary" :loading="loadingMore" @click="loadMore()">
          {{ $t('message.view_more') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import BeltsSelect from "@/components/inputs/BeltsSelect.vue";
import TagsCombo from "@/components/inputs/TagsCombo.vue";
import Api from "@/services/Api";

export default {
  data() {
    return {
      loading: true,
      loadingLists: true,
      users: [],
      total: 0,
      hasMore: false,
      skip: 0,
      loadingMore: false,
      availableLevels: [],
      availableSports: [],
      availableKeys: [],
      availableValues: [],
      availableLabels: null,
      keyFilter: null,
      valueFilter: null,
      sportFilter: null,
      levelFilter: null,
      labelFilter: null,
      beltFilter: null,
      genderFilter: null,
      genderOptions: [
        { text: this.$t('message.male'), value: 'male' },
        { text: this.$t('message.female'), value: 'female' },
      ]
    }
  },
  components: {
    TagsCombo,
    BeltsSelect
  },
  created() {
    this.getAcademyLists();
    this.searchUsers();
    this.getAvailableLabels();
  },
  watch: {
    labelFilter: {
      handler: function () {
        this.searchUsers();
      },
    },
    beltFilter: {
      handler: function () {
        this.searchUsers();
      },
    },
    genderFilter: {
      handler: function () {
        this.searchUsers();
      },
    },
  },
  methods: {
    getAvailableLabels() {
      Api()
        .get("admins/academy/" + this.$store.state.selectedAcademy + "/tags")
        .then((response) => {
          if (response.data) {
            this.availableLabels = response.data.sort();
          }
        })
        .catch((e) => {
          this.loading = false;
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    buildParams(skip = 0) {
      const params = { skip };
      if (this.keyFilter) params.key = this.keyFilter;
      if (this.valueFilter) params.value = this.valueFilter;
      if (this.sportFilter) params.sport = this.sportFilter;
      if (this.levelFilter) params.level = this.levelFilter;
      if (this.beltFilter) params.belt = this.beltFilter;
      if (this.labelFilter && this.labelFilter.length > 0) params.labels = this.labelFilter;
      if (this.genderFilter) params.gender = this.genderFilter;
      return params;
    },
    searchUsers() {
      this.skip = 0;
      this.loading = true;
      Api()
        .post(
          "admins/managedacademy/" +
          this.$store.state.managedAcademy +
          "/users/search", this.buildParams(0)
        )
        .then((response) => {
          if (response && response.data) {
            this.users = response.data.users;
            this.total = response.data.total;
            this.hasMore = response.data.hasMore;
            this.loading = false;
          }
        });
    },
    loadMore() {
      this.loadingMore = true;
      const nextSkip = this.skip + 100;
      Api()
        .post(
          "admins/managedacademy/" +
          this.$store.state.managedAcademy +
          "/users/search", this.buildParams(nextSkip)
        )
        .then((response) => {
          if (response && response.data) {
            this.users = this.users.concat(response.data.users);
            this.total = response.data.total;
            this.hasMore = response.data.hasMore;
            this.skip = nextSkip;
            this.loadingMore = false;
          }
        })
        .catch(() => { this.loadingMore = false; });
    },
    getAvailableValues() {
      if (!this.keyFilter) {
        this.getAcademyLists();
      } else {
        Api()
          .get(
            "admins/managedacademy/" +
            this.$store.state.managedAcademy +
            "/key-values/" + this.keyFilter + "/values"
          )
          .then((response) => {
            if (response && response.data) {
              this.availableValues = response.data.values;
              this.loadingLists = false;
            }
          });
      }
    },
    getAcademyLists() {
      Api()
        .get(
          "admins/managedacademy/" +
          this.$store.state.managedAcademy +
          "/lists"
        )
        .then((response) => {
          if (response && response.data) {
            this.availableLevels = response.data.levels;
            this.availableSports = response.data.sports;
            this.availableKeys = response.data.keys;
            this.availableValues = response.data.values;
            this.loadingLists = false;
          }
        });
    },
    goToUserPage(u) {
      if (u.type == 'member')
        this.navigateTo({ name: "viewmember", params: { memberid: u.userId } });
      else
        if (u.type == 'member')
          this.navigateTo({
            name: 'viewvisitor',
            params: { visitorId: u.memberDataId },
            crumbName: u.displayName || u.email,
          })
    }
  },
};
</script>
